import { useId } from 'react';

interface NulaLockupProps {
  height?: number;
  /**
   * Retained for API compat. The mark now ships the baked-in Aurora gradient,
   * so markColor no longer tints it.
   */
  markColor?: string;
  /** Wordmark ("Nula" letterforms) fill. Theme-adaptive so it reads in dark
   *  mode; the source art bakes #202020 but we keep it token-driven. */
  wordmarkColor?: string;
  className?: string;
}

/**
 * Nula full brand lockup — Aurora emblem + "Nula" wordmark side by side.
 * Matches the source lockup SVG (viewBox 0 0 775.83 224.52, ~3.45:1); the
 * emblem carries its exact baked gradient, the wordmark stays token-driven.
 */
export function NulaLockup({
  height = 28,
  markColor: _markColor,
  wordmarkColor = 'var(--on-surface)',
  className,
}: NulaLockupProps) {
  const gradientId = useId();
  return (
    <svg
      height={height}
      viewBox="0 0 775.83 224.52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Nula"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="166.94"
          y1="190.64"
          x2="37.43"
          y2="65.75"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#5ec8e5" />
          <stop offset="0.49" stopColor="#72d6c9" />
          <stop offset="1" stopColor="#a6e3f2" />
        </linearGradient>
      </defs>
      {/* Aurora emblem */}
      <path
        fill={`url(#${gradientId})`}
        d="M99.22,26.13C44.52,26.13,0,70.63,0,125.33s44.52,99.19,99.22,99.19,99.19-44.5,99.19-99.19S153.91,26.13,99.22,26.13ZM160.37,142.36c-5.89,3.18-11.97,6.48-17.01,11.45-5.79,5.75-10.41,13.59-15.29,21.91-9.3,15.85-18.14,30.83-34.32,30.83-.52,0-1.06,0-1.6-.05-30.69-1.84-60.21-25.16-70.17-55.45-7.94-24.1-4.24-50.17,10.18-71.47,14.09-20.85,36.28-33.71,60.87-35.26,1.77-.12,3.49-.16,5.23-.16,40.75,0,76.74,30.29,81.53,69.68,2.05,16.84-7.42,22-19.41,28.53Z"
      />
      {/* Wordmark — N U L A */}
      <path fill={wordmarkColor} d="M398.73,194.92h-26.08v-70.56c0-14.45-3.67-25.48-10.98-33.08-7.32-7.59-17.29-11.39-29.92-11.39s-22.61,3.8-29.92,11.39c-7.33,7.6-10.98,18.63-10.98,33.08v70.56h-26.08v-70.56c0-22.14,6.04-39.12,18.12-50.92,12.08-11.8,28.37-17.71,48.87-17.71s36.79,5.86,48.87,17.57c12.08,11.71,18.12,28.74,18.12,51.06v70.56Z"/>
      <path fill={wordmarkColor} d="M529.49,130.13V59.3h26.08v70.83c0,21.96-6.04,38.84-18.12,50.65-12.08,11.81-28.37,17.7-48.87,17.7s-36.79-5.85-48.87-17.57c-12.08-11.71-18.12-28.64-18.12-50.79V59.3h26.08v70.83c0,14.46,3.66,25.44,10.98,32.94,7.32,7.5,17.29,11.25,29.92,11.25s22.6-3.75,29.92-11.25c7.32-7.5,10.98-18.49,10.98-32.94"/>
      <rect fill={wordmarkColor} x="582.72" width="26.08" height="194.92" y="0"/>
      <path fill={wordmarkColor} d="M775.83,128.21v66.71h-23.89v-28.28h-.55c-4.21,9.7-10.94,17.43-20.18,23.2-9.24,5.76-20,8.64-32.26,8.64-18.67,0-34.78-6.4-48.32-19.22-13.55-12.81-20.31-30.2-20.31-52.16,0-20.68,6.81-37.75,20.45-51.2,13.63-13.45,31.16-20.18,52.57-20.18s38.85,6.68,52.3,20.04c13.45,13.36,20.18,30.84,20.18,52.44M669.59,161.15c8.6,8.79,19.86,13.18,33.77,13.18s25.16-4.39,33.77-13.18c8.6-8.78,12.9-20.13,12.9-34.04s-4.3-25.26-12.9-34.04c-8.6-8.79-19.86-13.18-33.77-13.18s-25.17,4.39-33.77,13.18c-8.6,8.78-12.9,20.13-12.9,34.04s4.3,25.26,12.9,34.04"/>
    </svg>
  );
}
